import store from '@/store/store'
import { setShowModalType } from '@/store/slices/modal/action'
import { ShowModalType } from '@/scenes/engine/common/constants'
import { customAmplitude } from '@/utils/customAmplitude'
import { hasUserDownloaded, recordUserDownload, recordUserVisit } from '@/utils/userActivity'
import { LIMITED_OFFER_DISCOUNT_PERCENT } from './limitedOfferConfig'
import {
  PRICE_WAIT_MS,
  getReadyLimitedOfferPrices,
  prefetchLimitedOfferPrices,
  waitForLimitedOfferPrices,
} from './limitedOfferPrices'
import {
  canShowLimitedOffer,
  readLimitedOfferHistory,
  recordLimitedOfferAcceptance,
  recordLimitedOfferImpression,
  writeLimitedOfferHistory,
  type LimitedOfferTrigger,
} from './limitedOfferHistory'

/** Startup modals (onboarding, What's New, the startup paywall) open asynchronously; let them go first. */
export const SESSION_START_DELAY_MS = 8000
/** Give up on the session-start offer if the screen stays busy this long. */
export const SESSION_START_WINDOW_MS = 2 * 60 * 1000
/** A download's own dialogs may hold the screen briefly before it is free. */
export const DOWNLOAD_WINDOW_MS = 30 * 1000

export type LimitedOfferDismissMethod = 'Close' | 'No Thanks' | 'Escape' | 'Scrim'

const TRIGGER_LABEL: Record<LimitedOfferTrigger, string> = {
  'paywall-close': 'Paywall Close',
  'return-visit': 'Return Visit',
  download: 'Download',
}

interface Session {
  userId: string
  returnedToday: boolean
  returnOffered: boolean
}

let session: Session | null = null
let pending: { userId: string; trigger: LimitedOfferTrigger; shown: boolean; unsubscribe: () => void } | null = null
let cancelSessionOffer: () => void = () => {}
let cancelDownloadOffer: () => void = () => {}
let priceWait: { cancelled: boolean } | null = null

function releasePending(): void {
  pending?.unsubscribe()
  pending = null
}

function currentUserId(): string | null {
  const user = store.getState().editor.user.user
  const id = user?.userId ?? user?.id
  return id == null ? null : String(id)
}

function isScreenFree(): boolean {
  const state = store.getState()
  return state.modal.showModalType === null &&
    !state.editor.user.openModalTryPremium.isOpen &&
    !state.editor.user.openModalOutOfCredits.isOpen &&
    !state.editor.user.introVideoDetail?.isOpen
}

function accountCreatedAt(): number | null {
  const joined = store.getState().editor.user.user?.date_joined
  const time = typeof joined === 'string' ? Date.parse(joined) : NaN
  return Number.isFinite(time) ? time : null
}

/** The user and session an offer may open for right now, or `null`. */
function admit(trigger: LimitedOfferTrigger): { userId: string } | null {
  if (pending || !isScreenFree()) return null
  const state = store.getState()
  const userId = currentUserId()
  if (!userId || state.editor.user.userIsPremium) return null
  const currentSession = session?.userId === userId ? session : null
  if (trigger !== 'paywall-close' && currentSession?.returnOffered) return null
  const history = readLimitedOfferHistory(userId)
  if (!history) return null
  const allowed = canShowLimitedOffer(history, trigger, {
    now: Date.now(),
    accountCreatedAt: accountCreatedAt(),
    returnedToday: currentSession?.returnedToday ?? false,
    hasDesignsOrDownloads: state.drafts.projects.length > 0 || hasUserDownloaded(userId),
  })
  return allowed ? { userId } : null
}

function open(trigger: LimitedOfferTrigger, userId: string): void {
  // One offer per moment: a waiting session-start or download offer must not follow this one.
  cancelSessionOffer()
  cancelDownloadOffer()
  const opened = { userId, trigger, shown: false, unsubscribe: () => {} }
  pending = opened
  store.dispatch(setShowModalType(ShowModalType.LIMITED_OFFER))
  // Another modal can take the slot before the offer is ever shown; don't keep it pending.
  opened.unsubscribe = store.subscribe(() => {
    if (pending === opened && !opened.shown && store.getState().modal.showModalType !== ShowModalType.LIMITED_OFFER) {
      releasePending()
    }
  })
}

/**
 * Opens the offer for a trigger when the user, the screen and the caps allow it.
 * The modal never shows placeholder prices: if they aren't fetched yet, it waits
 * up to PRICE_WAIT_MS, re-checks everything, and skips the moment on timeout.
 * Returns whether the offer opened or is waiting for its prices.
 */
export function maybeShowLimitedOffer(trigger: LimitedOfferTrigger): boolean {
  if (priceWait) return false
  const admitted = admit(trigger)
  if (!admitted) return false
  if (getReadyLimitedOfferPrices()) {
    open(trigger, admitted.userId)
    return true
  }
  const waiting = { cancelled: false }
  priceWait = waiting
  void waitForLimitedOfferPrices(PRICE_WAIT_MS).then(prices => {
    if (waiting.cancelled) return
    if (priceWait === waiting) priceWait = null
    const again = prices ? admit(trigger) : null
    if (again && again.userId === admitted.userId) open(trigger, again.userId)
  })
  return true
}

/** Waits until the screen is free, then tries the trigger once. Returns the cancel. */
function offerWhenScreenFree(trigger: LimitedOfferTrigger, delayMs: number, windowMs: number): () => void {
  let unsubscribe: () => void = () => {}
  let expiry: ReturnType<typeof setTimeout> | undefined
  let retry: ReturnType<typeof setTimeout> | undefined
  const stop = () => {
    unsubscribe()
    if (expiry !== undefined) clearTimeout(expiry)
    if (retry !== undefined) clearTimeout(retry)
  }
  const attempt = () => {
    if (!isScreenFree()) return
    stop()
    maybeShowLimitedOffer(trigger)
  }
  // Re-check after the current dispatch, so a close handler that frees the screen
  // finishes (and can open its own modal or offer) before this one runs.
  const scheduleAttempt = () => {
    if (retry !== undefined) clearTimeout(retry)
    retry = setTimeout(attempt, 0)
  }
  const start = setTimeout(() => {
    expiry = setTimeout(stop, windowMs)
    unsubscribe = store.subscribe(scheduleAttempt)
    attempt()
  }, delayMs)
  return () => {
    clearTimeout(start)
    stop()
  }
}

/**
 * Records this visit for the signed-in user and, on a return visit, offers the
 * modal once the startup modals have finished. Returns the cleanup.
 */
export function startLimitedOfferSession(): () => void {
  const onUser = () => {
    const userId = currentUserId()
    if (!userId || session?.userId === userId) return
    cancelSessionOffer()
    const returnedToday = recordUserVisit(userId)
    session = { userId, returnedToday, returnOffered: false }
    // Free users may see the offer this session; have its prices ready before it opens.
    if (!store.getState().editor.user.userIsPremium) prefetchLimitedOfferPrices()
    if (returnedToday) {
      cancelSessionOffer = offerWhenScreenFree('return-visit', SESSION_START_DELAY_MS, SESSION_START_WINDOW_MS)
    }
  }
  const unsubscribe = store.subscribe(onUser)
  onUser()
  return () => {
    unsubscribe()
    cancelSessionOffer()
  }
}

/** Trigger 1: the user closed the paywall without buying. */
export function maybeShowLimitedOfferAfterPaywallClose(): void {
  maybeShowLimitedOffer('paywall-close')
}

/** Called after every image or video download: records it, then tries trigger 2. */
export function noteLimitedOfferDownload({ showOffer = true }: { showOffer?: boolean } = {}): void {
  const userId = currentUserId()
  if (!userId) return
  recordUserDownload(userId)
  if (!showOffer) return
  cancelDownloadOffer()
  cancelDownloadOffer = offerWhenScreenFree('download', 0, DOWNLOAD_WINDOW_MS)
}

/** Called by the modal once it is on screen. Debug opens are not counted. */
export function markLimitedOfferShown(): void {
  if (!pending || pending.shown || currentUserId() !== pending.userId) return
  const history = readLimitedOfferHistory(pending.userId)
  if (!history) return
  const next = recordLimitedOfferImpression(history, pending.trigger, Date.now())
  writeLimitedOfferHistory(pending.userId, next)
  pending.shown = true
  // The return trigger is spent only once its offer is actually on screen; a
  // replaced offer leaves it available for the rest of the visit.
  if (pending.trigger !== 'paywall-close' && session?.userId === pending.userId) session.returnOffered = true
  customAmplitude('Special Offer Prompt', {
    Trigger: TRIGGER_LABEL[pending.trigger],
    Discount: LIMITED_OFFER_DISCOUNT_PERCENT,
    Impression: next.impressions,
  })
}

/** Called when the modal closes: accepted, dismissed with a method, or replaced (`null`). */
export function settleLimitedOffer(outcome: 'accepted' | LimitedOfferDismissMethod | null): void {
  const current = pending
  releasePending()
  if (!current?.shown || outcome === null || currentUserId() !== current.userId) return
  const properties = { Trigger: TRIGGER_LABEL[current.trigger], Discount: LIMITED_OFFER_DISCOUNT_PERCENT }
  if (outcome === 'accepted') {
    customAmplitude('Special Offer Accepted', properties)
    return
  }
  customAmplitude('Special Offer Dismissed', { ...properties, Method: outcome })
}

/**
 * The upgrade callback for the offer's paywall. Only a completed purchase stops
 * the campaign for the user who accepted; abandoning checkout leaves it running.
 */
export function limitedOfferPurchaseCallback(): (() => void) | undefined {
  const userId = currentUserId()
  if (!userId) return undefined
  return () => {
    const history = readLimitedOfferHistory(userId)
    if (history) writeLimitedOfferHistory(userId, recordLimitedOfferAcceptance(history, Date.now()))
  }
}

/** A debug open must not inherit a trigger's pending impression. */
export function clearPendingLimitedOffer(): void {
  releasePending()
}

/** An action that already showed an offer must not inherit queued marketing. */
export function cancelWaitingLimitedOffers(): void {
  if (priceWait) priceWait.cancelled = true
  priceWait = null
  cancelSessionOffer()
  cancelDownloadOffer()
}

/** Test-only reset of module state. */
export function resetLimitedOfferTriggerForTests(): void {
  cancelSessionOffer()
  cancelDownloadOffer()
  releasePending()
  if (priceWait) priceWait.cancelled = true
  priceWait = null
  session = null
}
